import type { JSX } from 'react';

import { Translate, useI18n } from '@change-corgi/core/react/i18n';
import { ButtonLink } from '@change-corgi/design-system-legacy/components/actions';
import { Icon } from '@change-corgi/design-system-legacy/components/icon';
import { Image } from '@change-corgi/design-system-legacy/components/media';
import { iconQuote } from '@change-corgi/design-system-legacy/icons';
import { Box, Container, Flex } from '@change-corgi/design-system-legacy/layout';
import { Text } from '@change-corgi/design-system-legacy/typography';

import type { LandingPageSlug } from 'src/app/pages/landing/template/shared/types';

type Props = {
	slug: LandingPageSlug;
	onCtaClick: () => void;
};

const ROWS = ['one', 'two', 'three'];

export function Body({ onCtaClick, slug }: Props): JSX.Element | null {
	const { translate } = useI18n();

	return (
		<Container px={8} my={64}>
			<Box
				sx={{
					display: 'grid',
					gridTemplateColumns: ['1fr', '7fr 5fr'],
					gap: 16,
				}}
				mx={[32, 16]}
			>
				<Box>
					<Text size="huge">
						<Translate value={`landing.${slug}.body.statistic_part_one`} />
						<Text color="primary-changeRed" size="huge" fontWeight="bold">
							{translate(`landing.${slug}.body.statistic_part_red`)}
						</Text>
						<Translate value={`landing.${slug}.body.statistic_part_two`} />
					</Text>
				</Box>
				<Box>
					<Text as="p">
						<Translate value={`landing.${slug}.body.statistic_detail`} />
					</Text>
				</Box>
			</Box>
			{ROWS.map((row, idx) => (
				<Box
					key={row}
					sx={{
						display: 'grid',
						gridTemplateColumns: ['1fr', idx % 2 ? '7fr 5fr' : '5fr 7fr'],
						gap: 32,
					}}
					mt={[85, 115]}
				>
					<Box sx={{ order: [undefined, idx % 2 ? 1 : 0], textAlign: 'center' }}>
						<Image
							sx={{
								boxShadow: '20px 20px 0px 0px #fff9df',
								maxWidth: ['80%', 355],
								margin: '0 auto',
							}}
							alt=""
							src={translate(`landing.${slug}.assets.image_${row}`)}
						/>
					</Box>
					<Box>
						<Text as="p" size={['large', 'huge']} fontWeight="bold" mx={16} mb={16}>
							<Translate value={`landing.${slug}.body.image_${row}.caption`} />
						</Text>
						<Flex mx={16}>
							<Box mr={16}>
								<Icon icon={iconQuote} sx={{ filter: 'opacity(40%)' }} size="xxl" />
							</Box>
							<Text as="p" size="large" sx={{ flex: 1 }}>
								<Translate value={`landing.${slug}.body.image_${row}.quote`} />
							</Text>
						</Flex>
					</Box>
				</Box>
			))}
			<Box
				variant="bordered"
				sx={{
					borderColor: 'primary-changeRed',
					borderWidth: 2,
				}}
				p={24}
				mt={[80, 100]}
				mx={[16, 40]}
			>
				<Flex
					sx={{
						alignItems: 'center',
						justifyContent: ['center', 'space-between'],
						flexDirection: ['column', 'row'],
					}}
				>
					<Text as="p" color="red.brand" fontWeight="bold" size="large" sx={{ textAlign: 'center' }} pb={[16, 0]}>
						<Translate value={`landing.${slug}.body.body_cta.ask`} />
					</Text>
					<ButtonLink
						sx={{ width: ['100%', 211] }}
						onClick={onCtaClick}
						to={`/start-a-petition?source_location=${slug}_landing_page`}
					>
						<Translate value={`landing.${slug}.body.body_cta.button`} />
					</ButtonLink>
				</Flex>
			</Box>
		</Container>
	);
}
